import React, { useEffect, useState } from "react";
import { useGetBestScorersQuery } from "state/api";
import {
  Container,
  Typography,
  Paper,
  Box,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Alert,
} from "@mui/material";
import { format } from "date-fns";

function ScoreDashboard({ empNo }) {
  // Format date helper
  const formatDate = (d, formatType = "long") => {
    const options =
      formatType === "short"
        ? { day: "numeric", month: "short", year: "numeric" }
        : { day: "numeric", month: "long", year: "numeric" };
    return d.toLocaleDateString("en-US", options);
  };

  // Fetch data from API
  const { data: bestScorers = [], isLoading, isError, error } = useGetBestScorersQuery();
  const [scoreboard, setScoreboard] = useState({
    daily: [],
    weekly: [],
    lastMonth: [],
  });

  useEffect(() => {
    if (!bestScorers.length) return;

    const today = new Date();
    const currentDay = today.getDate();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();

    // Track scores by date and total; track visits for scoring logic
    const scoresByDate = {};
    const totalScores = {};

    // Map EmpNo+MclCode to Set of visit dates to handle scoring:
    //    - first ever visit to doctor => score=4
    //    - repeat visit same day => score=6
    //    - repeat visits different day => score=4 (new first visit of that day)
    const visitTracker = new Map();

    for (const record of bestScorers) {
      const { EmpNo, NAME, HeadQrt, StateCd, MclCode, SUBDATE } = record;
      const dateObj = new Date(SUBDATE);
      const dateKey = formatDate(dateObj, "long");
      const dayKey = format(dateObj, "yyyy-MM-dd"); // day level key for repeat same-day visits
      const vMonth = dateObj.getMonth();
      const vYear = dateObj.getFullYear();

      const isCurrentMonth = vMonth === currentMonth && vYear === currentYear;
      const isPreviousMonth =
        (currentMonth === 0 && vMonth === 11 && vYear === currentYear - 1) ||
        (vMonth === currentMonth - 1 && vYear === currentYear);

      if (!isCurrentMonth && !isPreviousMonth) continue;

      const visitKey = `${EmpNo}-${MclCode}`;
      if (!visitTracker.has(visitKey)) {
        visitTracker.set(visitKey, new Set());
      }
      const daysVisited = visitTracker.get(visitKey);

      let score = 4; // default first visit for that day
      if (daysVisited.has(dayKey)) {
        // repeat visit same day to same doctor - score 6
        score = 6;
      }
      // Add this visit day to set
      daysVisited.add(dayKey);

      if (isCurrentMonth) {
        scoresByDate[dateKey] ??= {};
        scoresByDate[dateKey][EmpNo] ??= { name: NAME, HeadQrt, StateCd, score: 0 };
        scoresByDate[dateKey][EmpNo].score += score;

        totalScores[EmpNo] ??= { name: NAME, HeadQrt, StateCd, score: 0 };
        totalScores[EmpNo].score += score;
      }

      if (isPreviousMonth) {
        totalScores.lastMonth ??= {};
        totalScores.lastMonth[EmpNo] ??= { name: NAME, HeadQrt, StateCd, score: 0 };
        totalScores.lastMonth[EmpNo].score += score;
      }
    }

    // Utility - get top 3 scorers from map
    const getTop3 = (scoreMap) =>
      Object.entries(scoreMap)
        .sort(([, a], [, b]) => b.score - a.score)
        .slice(0, 3)
        .map(([EmpNo, info]) => ({ EmpNo, ...info }));

    // Aggregate score range for all or single employee
    const aggregateScoreRange = (startDay, endDay, employeeNo = null) => {
      const aggregated = {};
      for (let d = startDay; d <= endDay; d++) {
        const key = formatDate(new Date(currentYear, currentMonth, d));
        const dayScores = scoresByDate[key];
        if (!dayScores) continue;
        if (employeeNo) {
          if (dayScores[employeeNo]) {
            aggregated[employeeNo] ??= {
              name: dayScores[employeeNo].name,
              HeadQrt: dayScores[employeeNo].HeadQrt,
              StateCd: dayScores[employeeNo].StateCd,
              score: 0,
            };
            aggregated[employeeNo].score += dayScores[employeeNo].score;
          }
        } else {
          for (const emp in dayScores) {
            aggregated[emp] ??= {
              name: dayScores[emp].name,
              HeadQrt: dayScores[emp].HeadQrt,
              StateCd: dayScores[emp].StateCd,
              score: 0,
            };
            aggregated[emp].score += dayScores[emp].score;
          }
        }
      }
      return aggregated;
    };

    // Week definitions as fixed date ranges:
    const WEEK_RANGES = [
      { start: 1, end: 7 },
      { start: 8, end: 14 },
      { start: 15, end: 21 },
      { start: 22, end: 28 },
      { start: 29, end: new Date(currentYear, currentMonth + 1, 0).getDate() },
    ];

    // Find which week current day belongs to
    const getCurrentWeekIndex = () => WEEK_RANGES.findIndex(
      (w) => currentDay >= w.start && currentDay <= w.end
    );

    // Compute weekly top scorers for previous and current week using fixed week ranges
    const computeWeeklyTopScorers = () => {
      const currentWeekIdx = getCurrentWeekIndex();
      const weeklyResults = [];
      if (currentWeekIdx > 0) {
        const prevWeek = WEEK_RANGES[currentWeekIdx - 1];
        const prevWeekAgg = aggregateScoreRange(prevWeek.start, prevWeek.end);
        weeklyResults.push({
          label: `Top 3 scorers of previous week (${prevWeek.start}–${prevWeek.end})`,
          data: getTop3(prevWeekAgg),
        });
      }
      const currWeek = WEEK_RANGES[currentWeekIdx];
      const currWeekEnd = Math.min(currWeek.end, currentDay);
      const currWeekAgg = aggregateScoreRange(currWeek.start, currWeekEnd);
      weeklyResults.push({
        label: `Top 3 scorers of current week (${currWeek.start}–${currWeekEnd})`,
        data: getTop3(currWeekAgg),
      });
      return weeklyResults;
    };

    // Result object
    const result = { daily: [], weekly: [], lastMonth: [] };

    if (empNo) {
      // Show daily scores for employee
      if (currentDay === 1) {
        // If today is Day 1, return only Day 1 score
        const agg = aggregateScoreRange(1, 1, empNo);
        if (agg[empNo]) {
          result.daily.push({
            label: `Day 1 (${format(new Date(currentYear, currentMonth, 1), "yyyy-MM-dd")})`,
            data: [{ EmpNo: empNo, ...agg[empNo] }],
          });
        }
      } else if (currentDay >= 2 && currentDay <= 7) {
        // For Days 2 to 7, return scores for today and the previous day
        const empDailyScores = [];
        for (let day = currentDay; day >= currentDay - 1; day--) {
          const agg = aggregateScoreRange(day, day, empNo);
          if (agg[empNo]) {
            empDailyScores.push({
              label: `Day ${day} (${format(new Date(currentYear, currentMonth, day), "yyyy-MM-dd")})`,
              data: [{ EmpNo: empNo, ...agg[empNo] }],
            });
          }
        }
        result.daily = empDailyScores;
      }
    else {
        // Weekly employee scores after day 7
        const currentWeekIdx = getCurrentWeekIndex();
        const empWeeklyScores = [];
        if (currentWeekIdx > 0) {
          const prevWeek = WEEK_RANGES[currentWeekIdx - 1];
          const aggPrev = aggregateScoreRange(prevWeek.start, prevWeek.end, empNo);
          if (aggPrev[empNo]) {
            empWeeklyScores.push({
              label: `Previous week (${prevWeek.start}–${prevWeek.end})`,
              data: [{ EmpNo: empNo, ...aggPrev[empNo] }],
            });
          }
        }
        const currWeek = WEEK_RANGES[currentWeekIdx];
        const currWeekEnd = Math.min(currWeek.end, currentDay);
        const aggCurr = aggregateScoreRange(currWeek.start, currWeekEnd, empNo);
        if (aggCurr[empNo]) {
          empWeeklyScores.push({
            label: `Current week (${currWeek.start}–${currWeekEnd})`,
            data: [{ EmpNo: empNo, ...aggCurr[empNo] }],
          });
        }
        result.weekly = empWeeklyScores;
      }
      setScoreboard(result);
      return;
    }

    // When empNo NOT provided:
    // Custom daily behavior for the first 7 days:
    if (currentDay >= 1 && currentDay <= 7) {
      let dailyResults = [];
      if (currentDay === 1) {
        const agg = aggregateScoreRange(1, 1);
        dailyResults.push({
          label: `Top 3 scorers Day 1`,
          data: getTop3(agg),
        });
      } else {
        // days 2-7: show today and previous day top 3
        for (let i = 0; i < 2; i++) {
          let dayToShow = currentDay - i;
          if (dayToShow < 1) break;
          const agg = aggregateScoreRange(dayToShow, dayToShow);
          dailyResults.push({
            label: `Top 3 scorers Day ${dayToShow}`,
            data: getTop3(agg),
          });
        }
      }
      result.daily = dailyResults;
    } else {
      // days > 7: show weekly top scorers based on fixed week ranges
      result.weekly = computeWeeklyTopScorers();
    }

    // Always include last month top 3 if available
    if (totalScores.lastMonth) {
      result.lastMonth.push({
        label: "Top 3 scorers of last month",
        data: getTop3(totalScores.lastMonth),
      });
    }

    setScoreboard(result);
  }, [bestScorers, empNo]);

  if (isLoading)
    return (
      <Container sx={{ mt: 3 }}>
        <CircularProgress />
      </Container>
    );

  if (isError)
    return (
      <Container sx={{ mt: 3 }}>
        <Alert severity="error">Error loading data: {(error && error.message) || "Unknown error"}</Alert>
      </Container>
    );

  if (empNo) {
    return (
      <Container maxWidth="sm" sx={{ mt: 2 }}>
        {scoreboard.daily.length > 0 &&
          scoreboard.daily.map((section, i) => (
            <Paper
              key={`emp-daily-${i}`}
              elevation={3}
              sx={{
                mb: 1,
                p: 1,
                display: "flex",
                justifyContent: "space-between",
                backgroundColor: "rgba(255 255 255 / 0.1)",
                backdropFilter: "blur(15px)",
                borderRadius: 1,
              }}
            >
              <Typography variant="body1">{section.label}</Typography>
              <Typography variant="h6">{section.data[0].score} Runs</Typography>
            </Paper>
          ))}
        {scoreboard.weekly.length > 0 &&
          scoreboard.weekly.map((section, i) => (
            <Paper
              key={`emp-weekly-${i}`}
              elevation={3}
              sx={{
                mb: 1,
                p: 1,
                display: "flex",
                justifyContent: "space-between",
                backgroundColor: "rgba(255 255 255 / 0.1)",
                backdropFilter: "blur(15px)",
                borderRadius: 1,
              }}
            >
              <Typography variant="body1">{section.label}</Typography>
              <Typography variant="h6">{section.data[0].score} Runs</Typography>
            </Paper>
          ))}
        {scoreboard.daily.length === 0 && scoreboard.weekly.length === 0 && (
          <Alert severity="info">No scores available yet for employee {empNo}.</Alert>
        )}
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 3, mb: 5 }}>
      <Typography variant="h4" gutterBottom>
        Top Scorers
      </Typography>
      {!scoreboard.lastMonth.length && !scoreboard.daily.length && !scoreboard.weekly.length && (
        <Alert severity="info">No scores available for the current month yet.</Alert>
      )}
     {scoreboard.lastMonth.map((section, i) => (
  <Paper
    key={`last-${i}`}
    elevation={3}
    sx={{
      mb: 3,
      p: 3,
      backgroundColor: "rgba(255, 255, 255, 0.1)",
      backdropFilter: "blur(25px)",
      borderRadius: 2,
      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2)",
    }}
  >
    <Typography variant="h5" sx={{ mb: 2, color: "gold", textAlign: "center" }}>
      {section.label}
    </Typography>
    <List sx={{ minWidth: 280 }}>
      {section.data.map((emp) => (
        <ListItem key={emp.EmpNo} disablePadding sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.2)" }}>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold', color: "white" }}>{emp.name}</Typography>
                <Typography variant="body2" sx={{ color: "lightgray" }}>HQ: {emp.HeadQrt}</Typography>
                <Typography variant="body2" sx={{ color: "lightgray" }}>State: {emp.StateCd}</Typography>
                <Typography variant="body2" sx={{ color: "lightgray" }}>Score: {emp.score} Runs</Typography>
              </Box>
            }
          />
        </ListItem>
      ))}
    </List>
  </Paper>
))}
      {scoreboard.daily.map((section, i) => (
  <Paper
    key={`daily-${i}`}
    elevation={3}
    sx={{
      mb: 3,
      p: 3,
      backgroundColor: "rgba(255, 255, 255, 0.1)",
      backdropFilter: "blur(25px)",
      borderRadius: 2,
      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2)",
    }}
  >
    <Typography variant="h5" sx={{ mb: 2, color: "gold", textAlign: "center" }}>
      {section.label}
    </Typography>
    <List sx={{ minWidth: 280 }}>
      {section.data.map((emp) => (
        <ListItem key={emp.EmpNo} disablePadding sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.2)" }}>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold', color: "white" }}>{emp.name}</Typography>
                <Typography variant="body2" sx={{ color: "lightgray" }}>HQ: {emp.HeadQrt}</Typography>
                <Typography variant="body2" sx={{ color: "lightgray" }}>State: {emp.StateCd}</Typography>
                <Typography variant="body2" sx={{ color: "lightgray" }}>Score: {emp.score} Runs</Typography>
              </Box>
            }
          />
        </ListItem>
      ))}
    </List>
  </Paper>
))}
      {scoreboard.weekly.map((section, i) => (
  <Paper
    key={`weekly-${i}`}
    elevation={3}
    sx={{
      mb: 3,
      p: 3,
      backgroundColor: "rgba(255, 255, 255, 0.1)",
      backdropFilter: "blur(25px)",
      borderRadius: 2,
      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2)",
    }}
  >
    <Typography variant="h5" sx={{ mb: 2, color: "gold", textAlign: "center" }}>
      {section.label}
    </Typography>
    <List sx={{ minWidth: 280 }}>
      {section.data.map((emp) => (
        <ListItem key={emp.EmpNo} disablePadding sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.2)" }}>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold', color: "white" }}>{emp.name}</Typography>
                <Typography variant="body2" sx={{ color: "lightgray" }}>HQ: {emp.HeadQrt}</Typography>
                <Typography variant="body2" sx={{ color: "lightgray" }}>State: {emp.StateCd}</Typography>
                <Typography variant="body2" sx={{ color: "lightgray" }}>Score: {emp.score} Runs</Typography>
              </Box>
            }
          />
        </ListItem>
      ))}
    </List>
  </Paper>
))}
    </Container>
  );
}

export default ScoreDashboard;

